import { CommonModule } from '@angular/common';
import { Component, OnInit, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { MenuItem, MessageService } from 'primeng/api';
import { BreadcrumbModule } from 'primeng/breadcrumb';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { ProgressBarModule } from 'primeng/progressbar';
import { TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { ToastModule } from 'primeng/toast';
import { finalize, forkJoin } from 'rxjs';
import { PageDataPanelComponent } from '../../shared/page-layout/page-data-panel.component';
import { PageDetailHeroComponent } from '../../shared/page-layout/page-detail-hero.component';
import { PageFormSectionComponent } from '../../shared/page-layout/page-form-section.component';
import { PageMetric } from '../../shared/page-layout/page-layout.models';
import { PageMetricStripComponent } from '../../shared/page-layout/page-metric-strip.component';
import { PageShellComponent } from '../../shared/page-layout/page-shell.component';
import { CrmClient } from '../../crm/crm.model';
import { CrmService } from '../../crm/crm.service';
import { statusSeverity, warehousingBreadcrumb, warehousingHomeBreadcrumb } from '../warehousing-common';
import { WarehousingBillingDraft } from '../warehousing.model';
import { WarehousingService } from '../warehousing.service';
import { ExecutionSalesOrderButtonComponent } from '../../order/sales-order/execution-sales-order-button.component';
import { SessionStorageService } from '../../services/session-storage.service';

@Component({
  selector: 'app-warehousing-billing-draft-view',
  standalone: true,
  imports: [CommonModule, FormsModule, RouterModule, BreadcrumbModule, ButtonModule, InputTextModule, ProgressBarModule, TableModule, TagModule, ToastModule, PageDataPanelComponent, PageDetailHeroComponent, PageFormSectionComponent, PageMetricStripComponent, PageShellComponent, ExecutionSalesOrderButtonComponent],
  providers: [MessageService],
  templateUrl: './warehousing-billing-draft-view.component.html'
})
export class WarehousingBillingDraftViewComponent implements OnInit {
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private warehousingService = inject(WarehousingService);
  private crmService = inject(CrmService);
  private messageService = inject(MessageService);
  private session = inject(SessionStorageService);
  isLoading = false;
  isSaving = false;
  isEditMode = false;
  draft?: WarehousingBillingDraft;
  clients: CrmClient[] = [];
  breadcrumbItems: MenuItem[] = warehousingBreadcrumb('Billing Draft Details', 'Billing Drafts', '/warehousing/billing-drafts');
  homeBreadcrumb: MenuItem = warehousingHomeBreadcrumb();

  ngOnInit(): void {
    this.isEditMode = this.route.snapshot.routeConfig?.path?.startsWith('edit') || false;
    this.loadDraft();
  }

  loadDraft(): void {
    const id = Number(this.route.snapshot.paramMap.get('id'));
    this.isLoading = true;
    forkJoin({ drafts: this.warehousingService.getBillingDrafts(), clients: this.crmService.getClients() }).subscribe({
      next: response => {
        const draft = (response.drafts.data || []).find(row => row.id === id);
        this.draft = draft ? JSON.parse(JSON.stringify(draft)) : undefined;
        this.clients = response.clients || [];
        this.breadcrumbItems = warehousingBreadcrumb(this.draft?.draftNumber || 'Billing Draft Details', 'Billing Drafts', '/warehousing/billing-drafts');
        this.isLoading = false;
      },
      error: () => this.isLoading = false
    });
  }

  addManualLine(): void {
    if (!this.draft) return;
    this.draft.lines = this.draft.lines || [];
    this.draft.lines.push({ metric: 'SQM', quantityDays: 1, chargeableArea: 1, ratePerDay: 0, calculatedAmount: 0, editedAmount: 0, description: 'Manual storage adjustment', manualLine: true });
  }

  saveDraft(): void {
    if (!this.draft?.id || this.isSaving) return;
    this.isSaving = true;
    this.warehousingService.updateBillingDraft(this.draft).pipe(finalize(() => this.isSaving = false)).subscribe(resp => {
      if (!resp.success) return;
      this.draft = resp.data;
      this.messageService.add({ severity: 'success', summary: 'Draft Saved', detail: 'Billing draft was updated.' });
    });
  }

  postDraft(): void {
    if (!this.draft?.id || this.isSaving) return;
    this.isSaving = true;
    this.warehousingService.postBillingDraft(this.draft.id).pipe(finalize(() => this.isSaving = false)).subscribe(resp => {
      if (!resp.success) return;
      this.draft = resp.data;
      this.messageService.add({ severity: 'success', summary: 'Invoice Posted', detail: 'Storage invoice was posted.' });
    });
  }

  clientName(id?: number): string { return this.clients.find(client => client.id === id)?.companyName || '-'; }

  metrics(): PageMetric[] {
    return [
      { label: 'Calculated', value: `${this.formatAmount(this.draft?.totalCalculatedAmount)} ${this.draft?.currency || ''}`.trim() },
      { label: 'Editable Total', value: `${this.formatAmount(this.draft?.totalEditedAmount)} ${this.draft?.currency || ''}`.trim(), tone: 'info' },
      { label: 'Lines', value: this.draft?.lines?.length || 0 },
      { label: 'Invoice', value: this.draft?.invoiceNumber || '-', tone: this.draft?.invoiceNumber ? 'success' : 'default' }
    ];
  }

  statusSeverity = statusSeverity;

  owningCompanyId(): number | undefined {
    return this.session.getCompany()?.id || this.session.getCustomer()?.company?.id;
  }

  private formatAmount(value?: number): string {
    return new Intl.NumberFormat('en-US', { maximumFractionDigits: 2, minimumFractionDigits: 2 }).format(value || 0);
  }
}
